import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { subDays } from 'date-fns'
import { formatInTimeZone, toZonedTime } from 'date-fns-tz'
import { DUBLIN_TZ, dublinYmdWeekday, formatYmdInDublin } from './dublin-time'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatCurrency(amount: number, currency = 'EUR'): string {
  return new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency,
  }).format(amount)
}

export function formatDate(date: Date | string): string {
  const dateObj = date instanceof Date ? date : new Date(date)
  if (!(dateObj instanceof Date) || !isFinite(dateObj.getTime())) {
    return 'Invalid date'
  }
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Dublin',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(dateObj)
}

export function formatDateWithOrdinal(date: Date | string): string {
  const d = date instanceof Date ? date : new Date(date)
  if (!(d instanceof Date) || !isFinite(d.getTime())) {
    return 'Invalid date'
  }
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Dublin',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
  const parts = formatter.formatToParts(d)
  const day = Number(parts.find((p) => p.type === 'day')?.value)
  const month = parts.find((p) => p.type === 'month')?.value
  const year = parts.find((p) => p.type === 'year')?.value
  if (!Number.isFinite(day) || !month || !year) {
    return 'Invalid date'
  }
  const getOrdinalSuffix = (n: number): string => {
    const j = n % 10
    const k = n % 100
    if (j === 1 && k !== 11) return 'st'
    if (j === 2 && k !== 12) return 'nd'
    if (j === 3 && k !== 13) return 'rd'
    return 'th'
  }

  return `${day}${getOrdinalSuffix(day)} ${month} ${year}`
}

export function formatDateTime(date: Date | string): string {
  const dateObj = date instanceof Date ? date : new Date(date)
  if (!(dateObj instanceof Date) || !isFinite(dateObj.getTime())) {
    return 'Invalid date'
  }
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Dublin',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(dateObj)
}

export function formatTime(date: Date | string): string {
  const dateObj = date instanceof Date ? date : new Date(date)
  if (!(dateObj instanceof Date) || !isFinite(dateObj.getTime())) {
    return 'Invalid time'
  }
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Dublin',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(dateObj)
}

/** Ireland-local phrasing for when a ticket was last checked in (e.g. today at 14:30, yesterday at 01:15). */
export function formatFriendlyPreviousUse(iso: string | undefined | null): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (!(d instanceof Date) || !isFinite(d.getTime())) return null

  const eventYmd = formatYmdInDublin(d)
  const now = new Date()
  const todayYmd = formatYmdInDublin(now)
  const yesterdayYmd = formatYmdInDublin(subDays(now, 1))

  const timeStr = formatInTimeZone(d, DUBLIN_TZ, 'HH:mm')
  if (eventYmd === todayYmd) return `today at ${timeStr}`
  if (eventYmd === yesterdayYmd) return `yesterday at ${timeStr}`

  const yearNow = Number(todayYmd.slice(0, 4))
  const yearEv = Number(eventYmd.slice(0, 4))
  if (yearEv === yearNow) {
    return `${formatInTimeZone(d, DUBLIN_TZ, 'EEE d MMM')} at ${timeStr}`
  }
  return `${formatInTimeZone(d, DUBLIN_TZ, 'd MMM yyyy')} at ${timeStr}`
}

export function generateShortId(length = 8): string {
  // Use cryptographically strong randomness to avoid predictable IDs
  const crypto = require('crypto') as typeof import('crypto')
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  const bytes = crypto.randomBytes(length)
  let result = ''
  for (let i = 0; i < length; i++) {
    // Map byte to index in allowed chars
    const idx = bytes[i] % chars.length
    result += chars[idx]
  }
  return result
}

export function generateOrderId(): string {
  // Longer, higher-entropy order ID while keeping a compact form for display
  const crypto = require('crypto') as typeof import('crypto')
  // 10 bytes -> 80 bits of entropy, base32-ish mapping via allowed chars
  const raw = crypto.randomBytes(10)
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  let id = ''
  for (let i = 0; i < raw.length; i++) {
    id += chars[raw[i] % chars.length]
  }
  return id
}

export function generateAccessToken(): string {
  const crypto = require('crypto')
  return crypto.randomBytes(32).toString('hex')
}

export function formatOrderId(orderId: string): string {
  // Simple format with # prefix and uppercase
  return `#${orderId.toUpperCase()}`
}

export function slugify(text: string): string {
  return text
    .toString()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\w\-]+/g, '')
    .replace(/\-\-+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '')
}

export function buildTripSlug(trip: {
  id: string
  title?: string | null
  route?: { originStop: { name: string }; destinationStop: { name: string } } | null
  departureAt: Date | string
}): string {
  const base = trip.title
    ? slugify(trip.title)
    : trip.route
    ? slugify(`${trip.route.originStop.name} ${trip.route.destinationStop.name}`)
    : 'trip'
  return `${base}-${trip.id}`
}

export const SELLING_FAST_THRESHOLD = 0.3

export function availabilityLabel(remaining: number, capacity: number): { label: string; className: string } {
  if (remaining <= 0) return { label: 'Sold Out', className: 'text-red-600 dark:text-red-400 font-medium' }
  const ratio = capacity > 0 ? remaining / capacity : 0
  const soldRatio = capacity > 0 ? (capacity - remaining) / capacity : 0
  if (ratio <= 0.1) return { label: 'Almost Sold Out', className: 'text-red-600 dark:text-red-400 font-medium' }
  if (ratio <= 0.35) return { label: 'Running Low', className: 'text-amber-600 dark:text-amber-400 font-medium' }
  if (soldRatio >= SELLING_FAST_THRESHOLD) return { label: 'Selling Fast', className: 'text-orange-600 dark:text-orange-400 font-medium' }
  return { label: 'Available', className: 'text-green-600 dark:text-green-400 font-medium' }
}

// Additional utility functions for testing
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

export function formatPhoneNumber(phone: string): string {
  // Simple Irish phone number formatting
  let cleaned = phone.replace(/\D/g, '')
  
  // Handle different input formats
  if (cleaned.startsWith('353')) {
    cleaned = cleaned.substring(3)
  } else if (cleaned.startsWith('0')) {
    cleaned = cleaned.substring(1)
  }
  
  // Format as +353 XX XXX XXXX
  if (cleaned.length === 9) {
    return `+353 ${cleaned.substring(0, 2)} ${cleaned.substring(2, 5)} ${cleaned.substring(5)}`
  }
  
  return phone // Return original if can't format
}

export function generateSecureToken(length = 32): string {
  const crypto = require('crypto')
  return crypto.randomBytes(length).toString('hex')
}

export function calculateDuration(start: Date, end: Date): number {
  return Math.floor((end.getTime() - start.getTime()) / (1000 * 60)) // minutes
}

/**
 * Maps an instant to a Date whose getters reflect the Europe/Dublin wall clock
 * for that instant (date-fns-tz `toZonedTime`). Used by tests/helpers that need
 * Irish local clock fields, not server local time.
 */
export function convertToIrishTime(date: Date): Date {
  return toZonedTime(date, DUBLIN_TZ)
}

export function isValidTicketCode(code: string): boolean {
  // Format: MB-12345-ABC
  const ticketRegex = /^MB-\d{5}-[A-Z]{3}$/
  return ticketRegex.test(code)
}

export function isValidRouteCode(code: string): boolean {
  // Format: GAL-DUB or CORK-LIM
  const routeRegex = /^[A-Z]{3,5}-[A-Z]{3,5}$/
  return routeRegex.test(code)
}

export function isValidPassengerCount(count: number): boolean {
  return count > 0 && count <= 55 // Standard bus capacity
}

export function createAppError(code: string, message: string, context?: any) {
  return {
    code,
    message,
    context,
    timestamp: new Date()
  }
}

export function sanitizeErrorForUser(error: Error): string {
  const message = error.message.toLowerCase()
  
  // Remove sensitive information
  if (message.includes('password') || message.includes('token') || message.includes('secret')) {
    return 'A service is temporarily unavailable. Please try again later.'
  }
  
  if (message.includes('database') || message.includes('connection')) {
    return 'A service is temporarily unavailable. Please try again later.'
  }
  
  return 'An unexpected error occurred. Please try again later.'
}

export function transformTripForApi(trip: any) {
  return {
    id: trip.id,
    price: formatCurrency(trip.price),
    availableSeats: trip.capacity - trip.bookedSeats,
    departureTime: formatDateTime(trip.departureAt)
  }
}

export function calculateAvailabilityPercentage(bookedSeats: number, capacity: number): number {
  return Math.round(((capacity - bookedSeats) / capacity) * 100)
}

export function calculateDiscountedPrice(price: number, discountPercent: number): number {
  return Math.round(price * (100 - discountPercent) / 100)
}

export function isPeakTime(date: Date): boolean {
  if (!(date instanceof Date) || !isFinite(date.getTime())) return false
  const ymd = formatYmdInDublin(date)
  const day = dublinYmdWeekday(ymd)
  const hour = Number(formatInTimeZone(date, DUBLIN_TZ, 'H'))

  if (day === 5 && hour >= 17) return true
  if (day === 1 && hour >= 7 && hour <= 10) return true

  return false
}

export function calculateJourneySegments(stops: Array<{ id: string; name: string; etaMinutes: number }>) {
  const segments = []
  for (let i = 0; i < stops.length - 1; i++) {
    segments.push({
      from: stops[i].name,
      to: stops[i + 1].name,
      duration: stops[i + 1].etaMinutes - stops[i].etaMinutes
    })
  }
  return segments
}